import { createSlice } from "@reduxjs/toolkit";
import { fetchChat, fetchAllChats } from "./chatThunk.js";
import { fetchChatList } from "../contacts/contactThunk.js";

const normalizePhone = (phone) => (phone ? phone.replace(/^\+/, "") : null);

const chatSlice = createSlice({
  name: "chat",
  initialState: {
    chats: [], // Individual chat messages
    chatList: [], // DEPRECATED: Keeping for backward compatibility
    allChats: [], // CRITICAL: Chat list from /api/chat endpoint (project-specific)
    campaignAnalytics: [],
    selectedContact: null,
    isChatExpired: false,
    loading: false,
    isInitialized: false,
    isDashboardContentReady: false,
    error: null,
    unreadCounts: {},
    hasMoreMessages: true,
    currentPage: 0,
    ui: {
      OpenUserProfile: true,
      screenWidth: typeof window !== "undefined" ? window.innerWidth : 0,
      isOverlayOpen: false,
      showEmojiPicker: false,
      showMediaModal: false,
      showTemplates: false,
      recording: false,
      openNewChat: false,
    },
  },
  reducers: {
    // Only clear message-level state, not lists or selection
    clearChats: (state) => {
      state.chats = [];
      state.error = null;
      state.hasMoreMessages = true;
      state.currentPage = 0;
    },
    // Super reset when truly needed
    clearAllChatState: (state) => {
      state.chats = [];
      state.chatList = [];
      state.allChats = [];
      state.selectedContact = null;
      state.isChatExpired = false;
      state.error = null;
      state.unreadCounts = {};
      state.isInitialized = false;
      state.isDashboardContentReady = false;
      state.hasMoreMessages = true;
      state.currentPage = 0;
    },
    setSelectedContact: (state, action) => {
      state.selectedContact = action.payload;
      state.isChatExpired = action.payload?.isExpired || false;
      state.chats = [];
      state.hasMoreMessages = true;
      state.currentPage = 0;
    },
    clearSelectedContact: (state) => {
      state.selectedContact = null;
      state.isChatExpired = false;
      state.chats = [];
      state.hasMoreMessages = true;
      state.currentPage = 0;
    },
    incrementUnread: (state, action) => {
      const phone = action.payload?.replace(/^\+/, "");
      state.unreadCounts[phone] = (state.unreadCounts[phone] || 0) + 1;
    },
    clearUnread: (state, action) => {
      const phone = action.payload.replace(/^\+/, "");
      delete state.unreadCounts[phone];
    },
    addMessage: (state, action) => {
      if (state.selectedContact) {
        if (action.payload.tempId) {
          const index = state.chats.findIndex(
            (chat) => chat._id === action.payload.tempId
          );
          if (index !== -1) {
            // Preserve local file data to prevent flicker
            const existingFileDate =
              state.chats[index].message?.file?.data ||
              state.chats[index].file?.data;
            const finalMessage = { ...action.payload };

            if (existingFileDate) {
              if (finalMessage.message && finalMessage.message.file) {
                finalMessage.message.file.data = existingFileDate;
              }
              // Also check top-level file if structure varies
              if (finalMessage.file) {
                finalMessage.file.data = existingFileDate;
              }
            }

            state.chats[index] = finalMessage;
            return;
          }
        }

        // Avoid duplicates by checking ID
        if (!state.chats.find((chat) => chat._id === action.payload._id)) {
          state.chats.push(action.payload);
        }
      }
    },
    updateMessage: (state, action) => {
      const { tempId, finalMessage } = action.payload;
      const index = state.chats.findIndex((chat) => chat._id === tempId);
      if (index !== -1) {
        state.chats[index] = finalMessage;
      }
    },
    updateChatList: (state, action) => {
      state.chatList = action.payload;
    },
    setHasMoreMessages: (state, action) => {
      state.hasMoreMessages = action.payload;
    },
    setUi: (state, action) => {
      state.ui = { ...state.ui, ...action.payload };
    },
    setIsDashboardContentReady: (state, action) => {
      state.isDashboardContentReady = action.payload;
    },
    updateMessageStatus: (state, action) => {
      const { messageId, status, timestamp } = action.payload;

      const messageIndex = state.chats.findIndex((chat) => {
        const chatMessageId =
          chat.message?.messageId || chat.message?.id || chat._id || chat.id;

        return chatMessageId === messageId;
      });

      if (messageIndex !== -1) {
        state.chats[messageIndex].status = status;

        if (state.chats[messageIndex].message) {
          state.chats[messageIndex].message.status = status;
        }

        if (state.chats[messageIndex].statusHistory) {
          state.chats[messageIndex].statusHistory.push({
            status,
            changedAt: timestamp || new Date().toISOString(),
          });
        } else {
          state.chats[messageIndex].statusHistory = [
            {
              status,
              changedAt: timestamp || new Date().toISOString(),
            },
          ];
        }

        state.chats[messageIndex].updatedAt = new Date().toISOString();
      }
    },
    updateLatestMessagePreview: (state, action) => {
      const { phone, message, timestamp, status } = action.payload;
      const normalizedPhone = phone?.replace(/^\+/, "");
      
      if (!normalizedPhone) return;

      const chatIndex = state.allChats.findIndex(
        (chat) => (chat.phone && chat.phone.replace(/^\+/, "") === normalizedPhone) || 
                  (chat.contactId && String(chat.contactId) === normalizedPhone) ||
                  (chat._id && String(chat._id) === normalizedPhone)
      );

      if (chatIndex !== -1) {
        state.allChats[chatIndex] = {
          ...state.allChats[chatIndex],
          latestMessage: {
            ...state.allChats[chatIndex].latestMessage,
            message: message,
            timestamp: timestamp || new Date().toISOString(),
            status: status || "received"
          },
          updatedAt: timestamp || new Date().toISOString()
        };
        
        // Optionally sort allChats so the newest message jumps to top
        state.allChats.sort((a, b) => {
          const timeA = new Date(a.updatedAt || 0).getTime();
          const timeB = new Date(b.updatedAt || 0).getTime();
          return timeB - timeA;
        });
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // CRITICAL: Fetch All Chats (project-specific chat list from /api/chat)
      .addCase(fetchAllChats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllChats.fulfilled, (state, action) => {
        state.loading = false;
        state.isInitialized = true;
        // CRITICAL FIX: Store in allChats for ChatList component
        state.allChats = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAllChats.rejected, (state, action) => {
        state.loading = false;
        state.isInitialized = true;
        state.error = action.payload || "Failed to fetch all chats";
      });

    builder
      // Fetch Chat Messages
      .addCase(fetchChat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChat.fulfilled, (state, action) => {
        // Only update state if the fetched chat is for the currently selected contact
        if (
          state.selectedContact &&
          normalizePhone(action.meta.arg.phone) ===
            normalizePhone(state.selectedContact.phone)
        ) {
          state.loading = false;
          const messages = Array.isArray(action.payload) ? action.payload : [];

          if (action.meta.arg.page === 0) {
            state.chats = messages;
            state.hasMoreMessages = messages.length >= 15; // If we got 15 messages, there might be more
          } else {
            state.chats = [...messages, ...state.chats];
            state.hasMoreMessages = messages.length > 0; // If we got any messages on pagination, there might be more
          }

          if (messages.length > 0) {
            state.currentPage = action.meta.arg.page;
          }
        }
      })
      .addCase(fetchChat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch chat";
      });

    builder
      // Fetch Chat List (from contacts - keeping for backward compatibility)
      .addCase(fetchChatList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatList.fulfilled, (state, action) => {
        state.loading = false;
        state.chatList = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchChatList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch chat list";
      });
  },
});

export const {
  clearChats,
  clearAllChatState,
  setSelectedContact,
  clearSelectedContact,
  incrementUnread,
  clearUnread,
  addMessage,
  updateMessage,
  updateMessageStatus,
  updateChatList,
  setHasMoreMessages,
  setUi,
  setIsDashboardContentReady,
  updateLatestMessagePreview,
} = chatSlice.actions;

export default chatSlice.reducer;
